import { now } from '@calendly/datetime'
import moment from 'moment-timezone'

import { Periods, SchedulingMethods } from 'constants/meetings'
import { INVALID_SUPERGRAPH_SCOPE_TYPES, isValidSupergraphScopeType } from 'features/meetings/supergraph/scope_utils'
import { startOfIsoWeekMonday, WEEK_STARTS_ON_MONDAY } from 'services/iso_week_start'
import { STATUS_MAP } from 'services/search_api/constants'
import { startOfUsWeekSunday } from 'services/us_week_start'
import { buildListMeetingsScope } from 'shared/api/meetings/list_meetings_scope'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'

/**
 * Calendly + Google + Outlook. Used by the `meetingsPageList` (search-api) path, which still keys off
 * `kinds`; cmd `listMeetings` keys off `sources` instead (see {@link buildListMeetingsSources}).
 */
export const DEFAULT_SUPERGRAPH_MEETING_KINDS = Object.freeze(['CALENDLY', 'GOOGLE', 'OUTLOOK'])

/**
 * Routing predicate for the supergraph meetings list. cmd `listMeetings` cannot express keyword search
 * or the recap (meeting-intelligence) filter, so those route to the search-then-filter `meetingsPageList`.
 * All-teams (`page`) and individual team (`page_single`) use cmd `listMeetings` with
 * top-level `input.scope` (`PAGE` / `PAGE_SINGLE`).
 */
export const shouldUseMeetingsPageList = (uiFilters) => {
  if (!uiFilters) return false
  const hasKeyword = Boolean(uiFilters.searchKeyword?.trim?.())
  const hasRecapFilter = Boolean(uiFilters.meetingIntelligenceStatus)
  // Team scopes (all-teams + individual team) use cmd `listMeetings` + `input.scope`.
  // Do not resolve via `listMeetingsScopeType` here — that wrongly sent all-teams to meetingsPageList.
  return hasKeyword || hasRecapFilter
}

const buildSupergraphKindsFromUiSchedulingMethods = (schedulingMethods) => {
  if (schedulingMethods == null) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (!Array.isArray(schedulingMethods) || schedulingMethods.length === 0) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (schedulingMethods.includes(SchedulingMethods.ALL)) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  const kinds = []
  if (schedulingMethods.includes(SchedulingMethods.CALENDLY)) kinds.push('CALENDLY')
  if (schedulingMethods.includes(SchedulingMethods.GOOGLE)) kinds.push('GOOGLE')
  if (schedulingMethods.includes(SchedulingMethods.OUTLOOK)) kinds.push('OUTLOOK')
  if (kinds.length === 0) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  return kinds
}

export const applyFixedMeetingsOrganizerScope = (filter) => {
  filter.is_organizer = false
  filter.include_notetaker_ineligible_external_events = true
}

const toSupergraphDateTime = (dt) => {
  const asMoment = typeof dt?.toJSDate === 'function' ? moment(dt.toJSDate()) : moment(dt)
  return asMoment.format('YYYY-MM-DDTHH:mm:ssZ')
}

/**
 * Bounds for the this_week / last_week presets. Monday-first when the meetings page opts in via
 * `weekStartsOn` (SA-4357), otherwise the US Sunday–Saturday window.
 *
 * `weekTimezone` is the profile timezone. Anchoring on it keeps this window on the same week the
 * meetings page scrolls to and exports (`getPresetWeekRange`); a bare `now()` follows the browser
 * zone, which can already be on the other side of the Sunday/Monday boundary and land a week off.
 */
const presetWeekRange = (weekOffset, weekStartsOn, weekTimezone) => {
  const anchor = (now(weekTimezone) ?? now()).plus({ weeks: weekOffset })
  const start = weekStartsOn === WEEK_STARTS_ON_MONDAY
    ? startOfIsoWeekMonday(anchor)
    : startOfUsWeekSunday(anchor)
  const end = start.plus({ days: 6 }).endOf('day')
  return { start, end }
}

/** Full calendar range for a preset period (no today/past split clamping). */
const fullPeriodRangeFilter = (rangeStart, rangeEnd) => ({
  operation: 'BETWEEN',
  start: toSupergraphDateTime(rangeStart),
  end: toSupergraphDateTime(rangeEnd),
})

export const buildSupergraphTimeFilter = (uiFilters, { today, past, forCount, fromStartOfToday }) => {
  if (!uiFilters) return null
  const { period, startDate, endDate, pivotDate, weekStartsOn, weekTimezone } = uiFilters

  const addDateRangeFilterForCount = (rangeStart, rangeEnd) => ({
    operation: 'BETWEEN',
    start: moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ'),
    end: moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
  })

  if (forCount) {
    switch (period) {
      case Periods.UPCOMING:
        return null
      case Periods.FIXED:
        if (!startDate || !endDate) return null
        return addDateRangeFilterForCount(startDate, endDate)
      case Periods.THIS_WEEK: {
        const { start, end } = presetWeekRange(0, weekStartsOn, weekTimezone)
        return fullPeriodRangeFilter(start, end)
      }
      case Periods.LAST_WEEK: {
        const { start, end } = presetWeekRange(-1, weekStartsOn, weekTimezone)
        return fullPeriodRangeFilter(start, end)
      }
      case Periods.THIS_MONTH:
        return addDateRangeFilterForCount(moment().startOf('month'), moment().endOf('month'))
      case Periods.THIS_YEAR:
        return addDateRangeFilterForCount(moment().startOf('year'), moment().endOf('year'))
      case Periods.TODAY:
        return addDateRangeFilterForCount(moment().startOf('day'), moment().endOf('day'))
      default:
        return null
    }
  }

  const todayStart = moment().startOf('day')
  const startOfTodayStr = todayStart.format('YYYY-MM-DDTHH:mm:ssZ')
  const endOfTodayStr = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')

  // Pivot for the untoggled upcoming split — the date the user picked in the selector, else today.
  // Only the UPCOMING legs use it, so the feed can re-center on any day (past + future around it)
  // without affecting clamped period ranges (fixed / this_week / ...).
  const pivot = pivotDate ? moment(pivotDate, 'YYYY-MM-DD', true) : null
  const startOfPivotStr = pivot?.isValid() ? pivot.clone().startOf('day').format('YYYY-MM-DDTHH:mm:ssZ') : startOfTodayStr
  const endOfPivotStr = pivot?.isValid() ? pivot.clone().endOf('day').format('YYYY-MM-DDTHH:mm:ssZ') : endOfTodayStr

  const addDateRangeFilter = (rangeStart, rangeEnd) => {
    const formattedStartDate = moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ')
    const formattedEndDate = moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
    if (past) {
      const actualEndDate = moment(rangeEnd).isBefore(todayStart)
        ? formattedEndDate
        : endOfTodayStr
      return {
        operation: 'BETWEEN',
        start: formattedStartDate,
        end: actualEndDate,
      }
    }
    const actualStartDate = moment(rangeStart).isAfter(todayStart)
      ? formattedStartDate
      : startOfTodayStr
    return {
      operation: 'BETWEEN',
      start: actualStartDate,
      end: formattedEndDate,
    }
  }

  switch (period) {
    case Periods.UPCOMING: {
      if (today) {
        return {
          operation: 'BETWEEN',
          start: startOfPivotStr,
          end: endOfPivotStr,
        }
      }
      if (past) {
        return { operation: 'LT', start: startOfPivotStr }
      }
      // Single forward query for the default-preset toggle: include today (incl. earlier-today
      // meetings) plus all upcoming, so today + future load from one paginated stream.
      if (fromStartOfToday) {
        return { operation: 'GTE', start: startOfPivotStr }
      }
      return { operation: 'GTE', start: endOfPivotStr }
    }
    case Periods.FIXED:
      if (!startDate || !endDate) return null
      return addDateRangeFilter(startDate, endDate)
    case Periods.THIS_WEEK: {
      const { start, end } = presetWeekRange(0, weekStartsOn, weekTimezone)
      if (!today && !past) {
        return fullPeriodRangeFilter(start, end)
      }
      return addDateRangeFilter(start.toJSDate(), end.toJSDate())
    }
    case Periods.LAST_WEEK: {
      const { start, end } = presetWeekRange(-1, weekStartsOn, weekTimezone)
      if (!today && !past) {
        return fullPeriodRangeFilter(start, end)
      }
      return addDateRangeFilter(start.toJSDate(), end.toJSDate())
    }
    case Periods.THIS_MONTH:
      if (!today && !past) {
        return fullPeriodRangeFilter(moment().startOf('month'), moment().endOf('month'))
      }
      return addDateRangeFilter(moment().startOf('month'), moment().endOf('month'))
    case Periods.THIS_YEAR:
      if (!today && !past) {
        return fullPeriodRangeFilter(moment().startOf('year'), moment().endOf('year'))
      }
      return addDateRangeFilter(moment().startOf('year'), moment().endOf('year'))
    case Periods.TODAY:
      if (!today && !past) {
        return fullPeriodRangeFilter(moment().startOf('day'), moment().endOf('day'))
      }
      return addDateRangeFilter(moment().startOf('day'), moment().endOf('day'))
    default:
      return null
  }
}

/**
 * Snake-cased filter for the search-api `meetingsPageList` path (`MeetingsSearch_MeetingsFilter`).
 * Used by the keyword-search / recap / count builders in `meeting_search_supergraph_utils`.
 */
export const buildSupergraphFilterFromUiFilters = (uiFilters, { today, past, forCount, fromStartOfToday } = {}) => {
  if (!uiFilters) return null

  const {
    inviteeEmails,
    eventTypeIds,
    userIds,
    teamIds,
    role,
    ownerType,
    searchKeyword,
    meetingIntelligenceStatus,
    statusIds,
    schedulingMethods,
    scopeType,
    scopeId,
    uuid,
  } = uiFilters

  const filter = {}

  if (inviteeEmails?.length) filter.invitee_emails = inviteeEmails
  if (eventTypeIds?.length) filter.event_type_uuids = eventTypeIds
  if (userIds?.length) filter.user_uuids = userIds
  if (teamIds?.length) filter.team_uuids = teamIds

  if (role) filter.role = role
  if (ownerType) filter.owner_type = ownerType

  if (meetingIntelligenceStatus) {
    filter.meeting_intelligence_status = meetingIntelligenceStatus
  }
  if (searchKeyword) filter.search_keyword = searchKeyword

  const mappedFromRoute = mapScopeTypeToBackendValue(scopeType, scopeId)
  if (mappedFromRoute === 'all_users_and_teams') {
    if (scopeId) filter.scope_id = scopeId
  } else if (mappedFromRoute) {
    if (!INVALID_SUPERGRAPH_SCOPE_TYPES.has(mappedFromRoute)) {
      filter.scope_type = mappedFromRoute
      if (scopeId && mappedFromRoute !== 'my_calendly' && scopeId !== 'me') {
        filter.scope_id = scopeId
      }
    }
  } else if (isValidSupergraphScopeType(scopeType)) {
    filter.scope_type = scopeType
    if (scopeId) filter.scope_id = scopeId
  }

  if (uuid) filter.ids = [uuid]

  if (statusIds?.length) {
    const mapped = statusIds
      .map((id) => STATUS_MAP[id])
      .filter(Boolean)

    if (mapped.length === 1) {
      filter.status = mapped[0]
    } else if (mapped.length > 1) {
      filter.statuses = mapped
    }
  }

  filter.kinds = buildSupergraphKindsFromUiSchedulingMethods(schedulingMethods)

  const timeFilter = buildSupergraphTimeFilter(uiFilters, { today, past, forCount, fromStartOfToday })
  if (timeFilter) filter.start_time = timeFilter

  applyFixedMeetingsOrganizerScope(filter)
  return filter
}

/**
 * cmd `sources` ([`MeetingSource`]). Empty/all → omitted (both internal + external); Calendly only →
 * `[SCHEDULING]` (internal only); Google/Outlook only → `[EXTERNAL]`. `EXTERNAL` covers both connected
 * calendars, so a Calendly+external mix is treated as "both" (omitted).
 */
const buildListMeetingsSources = (schedulingMethods) => {
  if (!Array.isArray(schedulingMethods) || schedulingMethods.length === 0) return undefined
  if (schedulingMethods.includes(SchedulingMethods.ALL)) return undefined
  const hasCalendly = schedulingMethods.includes(SchedulingMethods.CALENDLY)
  const hasExternal = schedulingMethods.includes(SchedulingMethods.GOOGLE)
    || schedulingMethods.includes(SchedulingMethods.OUTLOOK)
  if (hasCalendly && !hasExternal) return ['SCHEDULING']
  if (!hasCalendly && hasExternal) return ['EXTERNAL']
  return undefined
}

/**
 * cmd `listMeetings` has no status enum — only `cancelled` / `hasNoShows` / `hasReschedules` booleans.
 * Map the common single-status selections; mixed active+canceled selections leave `cancelled` unset.
 */
const buildListMeetingsStatusFlags = (statusIds) => {
  if (!Array.isArray(statusIds) || statusIds.length === 0) return {}
  const set = new Set(statusIds)
  const flags = {}
  if (set.has('no_show')) flags.hasNoShows = true
  if (set.has('rescheduled')) flags.hasReschedules = true
  if (set.size === 1) {
    if (set.has('active')) flags.cancelled = false
    else if (set.has('canceled')) flags.cancelled = true
  }
  return flags
}

/** camelCased filter for cmd `listMeetings` (`Meetings_ListMeetingsFilter`). */
const buildListMeetingsFilterFromUiFilters = (uiFilters, { today, past, forCount, fromStartOfToday } = {}) => {
  if (!uiFilters) return null

  const {
    inviteeEmails,
    eventTypeIds,
    role,
    statusIds,
    schedulingMethods,
    uuid,
    utmCampaign,
    utmContent,
    utmMedium,
    utmSource,
    utmTerm,
  } = uiFilters

  const filter = {}

  if (inviteeEmails?.length) filter.inviteeEmails = inviteeEmails
  if (eventTypeIds?.length) filter.eventTypeUuids = eventTypeIds
  if (uuid) filter.uuids = [uuid]

  const sources = buildListMeetingsSources(schedulingMethods)
  if (sources) filter.sources = sources

  Object.assign(filter, buildListMeetingsStatusFlags(statusIds))

  if (Array.isArray(role) && role.length) {
    if (role.includes('HOST') || role.includes('COHOST')) {
      filter.isHost = true
    } else if (role.includes('ATTENDEE')) {
      filter.isHost = false
    }
  }

  if (utmCampaign?.length) filter.utmCampaigns = utmCampaign
  if (utmContent?.length) filter.utmContents = utmContent
  if (utmMedium?.length) filter.utmMediums = utmMedium
  if (utmSource?.length) filter.utmSources = utmSource
  if (utmTerm?.length) filter.utmTerms = utmTerm

  const timeFilter = buildSupergraphTimeFilter(uiFilters, { today, past, forCount, fromStartOfToday })
  if (timeFilter) filter.startTime = timeFilter

  return filter
}

const buildListMeetingsInputFields = (uiFilters, filterOptions = {}) => {
  const filter = buildListMeetingsFilterFromUiFilters(uiFilters, filterOptions)
  const scope = buildListMeetingsScope(uiFilters)
  // MY_CALENDLY already scopes to the caller's meetings; isHost would incorrectly
  // exclude cohost / permission / attendee meetings that still belong in this scope.
  if (scope.kind === 'MY_CALENDLY' && filter?.isHost != null) {
    delete filter.isHost
  }
  return {
    filter,
    scope,
  }
}

/**
 * cmd `listMeetings` input (`Meetings_ListMeetingsInput`) for the default meetings page / scope /
 * non-recap filter list slices.
 */
export const buildSupergraphInput = (uiFilters, { today, past, first, after, order, fromStartOfToday }) => ({
  ...buildListMeetingsInputFields(uiFilters, { today, past, fromStartOfToday }),
  pagination: {
    first,
    after: after || null,
  },
  orderBy: {
    startTime: order,
  },
})

/** Page size for supergraph filter counts — fetch nodes and count the returned meetings array. */
const SUPERGRAPH_COUNT_PAGE_SIZE = 10_000

const COUNT_STATUS_IDS = Object.freeze(['active', 'canceled', 'no_show', 'rescheduled'])

const buildMeetingsPageListCountInput = (uiFilters) => ({
  filter: buildSupergraphFilterFromUiFilters(uiFilters, { forCount: true }),
  pagination: {
    first: SUPERGRAPH_COUNT_PAGE_SIZE,
    after: null,
  },
  orderBy: {
    start_time: 'ASC',
  },
})

const buildListMeetingsCountInput = (uiFilters) => ({
  ...buildListMeetingsInputFields(uiFilters, { forCount: true }),
  pagination: {
    first: SUPERGRAPH_COUNT_PAGE_SIZE,
    after: null,
  },
  orderBy: {
    startTime: 'ASC',
  },
})

/**
 * cmd `listMeetings` encodes status as booleans, so OR selections (e.g. active + canceled) need one
 * count query per status; the API layer sums the results.
 */
export const buildListMeetingsCountInputs = (uiFilters) => {
  const statusIds = uiFilters?.statusIds
  const distinctStatuses = Array.isArray(statusIds)
    ? [...new Set(statusIds.filter((id) => id && id !== 'all' && COUNT_STATUS_IDS.includes(id)))]
    : []

  if (distinctStatuses.length <= 1) {
    return [buildListMeetingsCountInput(uiFilters)]
  }

  return distinctStatuses.map((statusId) => buildListMeetingsCountInput({
    ...uiFilters,
    statusIds: [statusId],
  }))
}

/**
 * Supergraph filter-count request: route to `listMeetings` (default) or `meetingsPageList` (search/recap).
 * Returns `{ path, inputs }` for the RTK count endpoint (`inputs` is one or more GraphQL variable payloads).
 */
export const buildSupergraphCountRequest = (uiFilters) => {
  if (shouldUseMeetingsPageList(uiFilters)) {
    return {
      path: 'meetingsPageList',
      inputs: [buildMeetingsPageListCountInput(uiFilters)],
    }
  }

  return {
    path: 'listMeetings',
    inputs: buildListMeetingsCountInputs(uiFilters),
  }
}

const lazyArgsStartTimeFilter = (args) => args?.filter?.startTime ?? args?.filter?.start_time

export const doesSupergraphLazyArgsMatchFixedVisibleRange = (originalArgs, visibleRangeUiVars) => {
  if (!visibleRangeUiVars || visibleRangeUiVars.period !== Periods.FIXED) return false
  const expected = buildListMeetingsFilterFromUiFilters(visibleRangeUiVars, {})
  const lazyPageStartTimeFilter = lazyArgsStartTimeFilter(originalArgs)
  const visibleRangeStartTimeFilter = expected?.startTime
  if (!lazyPageStartTimeFilter || !visibleRangeStartTimeFilter) return false
  return lazyPageStartTimeFilter.operation === visibleRangeStartTimeFilter.operation
    && lazyPageStartTimeFilter.start === visibleRangeStartTimeFilter.start
    && lazyPageStartTimeFilter.end === visibleRangeStartTimeFilter.end
}

/**
 * Periods where future vs past queries apply distinct start_time filters.
 * For all_time / past (and unknown), both legs are unbounded and overlap — only past should be merged.
 */
export const usesSplitFuturePastTimeFilters = (period) => (
  period === Periods.UPCOMING
  || period === Periods.FIXED
  || period === Periods.THIS_WEEK
  || period === Periods.LAST_WEEK
  || period === Periods.THIS_MONTH
  || period === Periods.THIS_YEAR
  || period === Periods.TODAY
)

export const isPreviousPageLazyArgs = (args, isSupergraphEnabled) => {
  if (!args) return false
  if (isSupergraphEnabled) {
    const order = args.orderBy?.startTime ?? args.orderBy?.start_time
    return order === 'DESC'
  }
  return Boolean(args.isPrevious || args.past)
}
